import { useState, type FormEvent } from "react";

import { platformApi } from "../../api/platformClient";
import { startSession } from "../../api/platformSession";
import { readError } from "../../api/client";
import { CodeInput } from "../../components/CodeInput";
import { ErrorNote } from "../../components/ErrorNote";
import { QrCode } from "../../components/QrCode";

/**
 * Signing in to the Healtrics console (ADR-024).
 *
 * Password, then the authenticator code, and no clinic step: this session has
 * no clinic and cannot acquire one. The absence is the boundary, not an
 * omission (ADR-021, ADR-024 section 3).
 *
 * It looks unlike the clinic portal on purpose. The two are served from one
 * origin for now, and somebody switching between them in a demonstration must
 * never be in doubt which one is in front of them.
 */

type Step =
  | { name: "password" }
  | { name: "enrol"; totpToken: string; uri: string; secret: string }
  | { name: "code"; totpToken: string };

export function PlatformSignIn() {
  const [step, setStep] = useState<Step>({ name: "password" });
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  async function submitPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);

    const { data, error: failed } = await platformApi.POST("/platform/auth/login", {
      body: {
        email: String(form.get("email") ?? ""),
        password: String(form.get("password") ?? ""),
      },
    });
    setBusy(false);

    if (failed || !data?.totp_token) {
      setError(readError(failed).message);
      return;
    }

    if (data.totp_enrolment_required) {
      await startEnrolment(data.totp_token);
      return;
    }
    setStep({ name: "code", totpToken: data.totp_token });
  }

  /** A request with no code asks for the provisioning URI. */
  async function startEnrolment(totpToken: string) {
    const { data, error: failed } = await platformApi.POST("/platform/auth/totp", {
      body: { totp_token: totpToken },
    });

    if (failed || !data?.provisioning_uri || !data.secret) {
      setError(readError(failed).message);
      return;
    }
    setStep({
      name: "enrol",
      totpToken,
      uri: data.provisioning_uri,
      secret: data.secret,
    });
  }

  async function submitCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (step.name === "password") return;
    setBusy(true);
    setError(null);

    const { data, error: failed } = await platformApi.POST("/platform/auth/totp", {
      body: { totp_token: step.totpToken, code },
    });
    setBusy(false);

    if (failed || !data?.access_token || !data.refresh_token) {
      setError(readError(failed).message);
      return;
    }

    startSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      full_name: data.full_name,
    });
  }

  function restart() {
    setError(null);
    setCode("");
    setStep({ name: "password" });
  }

  const codeField = (
    <>
      <CodeInput value={code} onChange={setCode} autoFocus invalid={!!error} />
      <ErrorNote message={error} />
      <button type="submit" disabled={busy || code.length < 6}>
        {busy ? "Checking…" : "Sign in"}
      </button>
      <button type="button" className="link" onClick={restart}>
        Start again
      </button>
    </>
  );

  return (
    <div className="plat-signin">
      <div className="card plat-signin-card">
        <p className="plat-mark">
          <span>HEALTRICS</span>
          <span className="plat-tag">Admin</span>
        </p>

        {step.name === "password" && (
          <form onSubmit={submitPassword}>
            <h1 className="signin-title">Healtrics console</h1>
            <p className="lead">
              Not a clinic. This sits above every clinic company, and it reaches
              no patient record in any of them.
            </p>

            <label htmlFor="email">Work email</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              placeholder="you@healtrics.com"
              required
            />

            <label htmlFor="password">Password</label>
            <div className="field-with-action">
              <input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                className="field-action"
                onClick={() => setShowPassword((on) => !on)}
                aria-pressed={showPassword}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>

            <ErrorNote message={error} />
            <button type="submit" className="wide" disabled={busy}>
              {busy ? "Checking…" : "Continue →"}
            </button>
          </form>
        )}

        {step.name === "enrol" && (
          <form onSubmit={submitCode}>
            <p className="lead">
              Set up your authenticator app. Scan this once, then enter the code
              it shows.
            </p>
            <QrCode value={step.uri} />
            <p className="muted">
              If you cannot scan, type this key into the app instead:
              <br />
              <code className="secret">{step.secret}</code>
            </p>
            {codeField}
          </form>
        )}

        {step.name === "code" && (
          <form onSubmit={submitCode}>
            <p className="lead">
              Enter the six-digit code from your authenticator app.
            </p>
            {codeField}
          </form>
        )}
      </div>
    </div>
  );
}
